// Shared types, constants, and labels for the License Economics feature.
// License Economics records are reusable *scenario inputs*. Managing them must
// never trigger a Forecast refresh or a stale-forecast warning.

import type {
  ContractualTrueUpTiming,
  LicenseEconomicsInputStatus,
  LicenseModel,
  LicenseRateTier,
} from "@workspace/api-client-react";

// Actor threaded into every mutation (matches Scenarios convention).
export const LE_ACTOR = "controller@tekledger.app";

export const LICENSE_MODEL_LABELS: Record<LicenseModel, string> = {
  seat_based: "Seat-based",
  usage_based: "Usage-based",
  hybrid: "Hybrid",
};

export const LICENSE_MODEL_ORDER: LicenseModel[] = [
  "seat_based",
  "usage_based",
  "hybrid",
];

export const TRUE_UP_LABELS: Record<ContractualTrueUpTiming, string> = {
  none: "No contractual true-up",
  annual: "Annual true-up",
  contractual: "At contract end / renewal",
};

export function summarizeTiers(
  tiers: LicenseRateTier[] | undefined,
  fallbackRate: number,
  unit: string
): string {
  if (!tiers?.length) return `Base ${formatRate(fallbackRate)}/${unit}`;
  const schedules = new Set(
    tiers.map((tier) => `${tier.effectiveFiscalYear}-${tier.effectivePeriod}`)
  ).size;
  return `${tiers.length} tier${tiers.length === 1 ? "" : "s"} · ${schedules} effective schedule${schedules === 1 ? "" : "s"}`;
}

export function summarizeTrueUp({
  trueUpTiming = "none",
  trueUpFiscalYear,
  trueUpFiscalPeriod,
  trueUpAmount = 0,
}: {
  trueUpTiming?: ContractualTrueUpTiming;
  trueUpFiscalYear?: number | null;
  trueUpFiscalPeriod?: number | null;
  trueUpAmount?: number;
}): string {
  if (trueUpTiming === "none") return TRUE_UP_LABELS.none;
  const timing =
    trueUpTiming === "annual" && trueUpFiscalPeriod
      ? `Annual · P${trueUpFiscalPeriod}`
      : trueUpTiming === "contractual" && trueUpFiscalYear && trueUpFiscalPeriod
        ? `Contractual · FY${trueUpFiscalYear} P${trueUpFiscalPeriod}`
      : TRUE_UP_LABELS[trueUpTiming];
  return `${timing} · ${new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(trueUpAmount)}`;
}

function formatRate(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 4,
  }).format(value);
}

export const LE_STATUS_LABELS: Record<LicenseEconomicsInputStatus, string> = {
  draft: "Draft",
  approved: "Approved",
  retired: "Retired",
};

// Maps a record status to the shared `.source-status` tone modifier used across
// the app (authoritative = green, missing = amber, loading/neutral otherwise).
export function statusTone(status: string | undefined): string {
  if (status === "approved") return "authoritative";
  if (status === "retired") return "missing";
  return "loading";
}

// Scope threaded from the page (fiscalYear + departmentCode derive from URL).
export interface LicenseScope {
  companyId: number;
  fiscalYear: number;
  departmentCode: string;
}
